import React from 'react';
import { useTranslation } from 'react-i18next';
import { FarmBenchmarkService } from './FarmBenchmarkService';
import { FarmBenchmark } from '../models/farm-benchmark';
import { extractApiErrorMessage, notifications } from '../utils';

interface UseFarmBenchmarkResult {
  benchmark: FarmBenchmark | null;
  isBusy: boolean;
  /** True once a fetch has settled, successfully or not - lets callers tell "failed" from "not asked yet". */
  loaded: boolean;
  reload: () => void;
}

/**
 * Loads the farm benchmark card.
 *
 * No period parameter, unlike the other dashboard widgets: the benchmark is a nightly snapshot over a fixed
 * twelve months, so the dashboard's period select does not apply to it. `farmId` picks the source as it does
 * for them - omitted, the signed-in business unit; set, the farm the admin farms overview opened.
 */
export const useFarmBenchmark = (farmId?: string): UseFarmBenchmarkResult => {
  const { t } = useTranslation();
  const [benchmark, setBenchmark] = React.useState<FarmBenchmark | null>(null);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    const service = new FarmBenchmarkService();
    // The farm can change while a request is in flight; ignore anything but the newest one.
    let cancelled = false;

    setIsBusy(true);

    service
      .getBenchmark(farmId)
      .then((result) => {
        if (cancelled) return;
        setBenchmark(result);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setBenchmark(null);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (cancelled) return;
        setIsBusy(false);
        setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [farmId, reloadToken]);

  const reload = React.useCallback(() => setReloadToken((token) => token + 1), []);

  return { benchmark, isBusy, loaded, reload };
};

export default useFarmBenchmark;
